import React from 'react';
import { UserPlus, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Renders contact share message as a card.
 * Shows name and avatar; if current user doesn't have this person as friend, shows "Arkadaş ekle" button.
 */
const ContactCardMessage = ({ message, isOwnMessage, onOpenFriendInfo, onAddFriend }) => {
  const { t } = useTranslation('chat');
  const { user } = useAuth();
  const { friends } = useChat();

  const contactUserId = message?.contact_user_id || message?.contactUserId;
  const contactEmail = message?.contact_email || message?.contactEmail;
  const name = message?.contact_name || message?.contactName || t('contactCard.unnamedContact');
  let avatarUrl = message?.contact_avatar_url || message?.contactAvatarUrl || null;
  if (avatarUrl && !avatarUrl.startsWith('http')) {
    try {
      const { getApiBaseUrl } = require('../../services/cloudApi');
      const base = getApiBaseUrl();
      if (base && typeof base === 'string') {
        const baseUrl = base.replace('/api', '');
        avatarUrl = `${baseUrl}${avatarUrl.startsWith('/') ? '' : '/'}${avatarUrl}`;
      }
    } catch (_) {}
  }

  const isMe = String(contactUserId) === String(user?.id);
  const friend = friends.find(
    (f) =>
      (f.email && f.email === contactEmail) ||
      String(f.user_id || f.id) === String(contactUserId)
  );
  const isFriend = !!friend && !friend.isDeleted;

  const handleCardClick = () => {
    if (isMe) return;
    if (isFriend && onOpenFriendInfo) {
      onOpenFriendInfo(friend.email || friend.id);
    }
  };

  const handleAddFriendClick = (e) => {
    e.stopPropagation();
    if (isMe || isFriend || !onAddFriend || !contactEmail) return;
    onAddFriend(contactEmail);
  };

  const initials = name.slice(0, 2).toUpperCase();
  const showAddFriend = !isFriend && !isMe && onAddFriend && contactEmail;

  const cardBg = isOwnMessage ? 'bg-[#1d3557] border-[#3d8ef0]/25' : 'bg-[var(--color-bg-muted)] border-white/[0.09]';

  return (
    <div className={`rounded-2xl border ${cardBg} overflow-hidden min-w-[220px] max-w-[280px]`}>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.06]">
        <User className="w-4 h-4 flex-shrink-0 text-[#3d8ef0]" />
        <span className="text-[11px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">{t('contactCard.header')}</span>
      </div>
      {/* Body */}
      <div className="px-4 py-3">
        <div
          role={isFriend ? 'button' : undefined}
          onClick={isFriend ? handleCardClick : undefined}
          className={`flex items-center gap-3 ${isFriend ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-12 h-12 rounded-full flex items-center justify-center font-semibold text-base flex-shrink-0 bg-[#2563eb]/15 text-[#3d8ef0]">
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[var(--color-text-primary)] truncate">{name}</p>
            {contactEmail && (
              <p className="text-xs text-[var(--color-text-muted)] truncate mt-0.5">{contactEmail}</p>
            )}
          </div>
        </div>
        {showAddFriend && (
          <button
            type="button"
            onClick={handleAddFriendClick}
            className="mt-3 inline-flex items-center justify-center gap-1.5 w-full py-2 px-3 rounded-xl text-xs font-semibold bg-[#2563eb] hover:bg-[#3d8ef0] text-white transition-colors"
          >
            <UserPlus className="w-3.5 h-3.5" />
            {t('contactCard.addFriend')}
          </button>
        )}
      </div>
    </div>
  );
};

export default ContactCardMessage;
